import { useMemo, useState } from "react";
import {
  BadgeTurkishLira,
  Calculator,
  CheckCircle2,
  ExternalLink,
  FileSpreadsheet,
  Info,
  RotateCcw,
  ShieldCheck,
  Sparkles,
  TrendingDown
} from "lucide-react";
import {
  EDUCATION_OPTIONS,
  INCENTIVE_OPTIONS,
  OFFICIAL_SOURCES,
  PAYROLL_RULES,
  calculatePayrollProjection,
  createDefaultPayrollRows
} from "../helpers/payrollIncentiveCalculator";

const formatMoney = (value) =>
  new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(Number(value) || 0);

const formatPercent = (value) => `%${Math.round((Number(value) || 0) * 100)}`;

function IncentiveCalculatorView() {
  const [year, setYear] = useState(2026);
  const [code, setCode] = useState("04691");
  const [education, setEducation] = useState("other");
  const [manufacturing, setManufacturing] = useState(false);
  const [startingCumulativeTaxBase, setStartingCumulativeTaxBase] = useState(0);
  const [bulkAmount, setBulkAmount] = useState(50000);
  const [bulkType, setBulkType] = useState("gross");
  const [rows, setRows] = useState(() => createDefaultPayrollRows(50000, "gross"));

  const result = useMemo(
    () =>
      calculatePayrollProjection({
        year,
        code,
        rows,
        education,
        manufacturing,
        startingCumulativeTaxBase
      }),
    [code, education, manufacturing, rows, startingCumulativeTaxBase, year]
  );
  const rules = PAYROLL_RULES[year];
  const researchCode = ["04691", "05746"].includes(code);

  const updateRow = (index, field, value) => {
    setRows((current) =>
      current.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row
      )
    );
  };

  const applyToAllMonths = () => {
    setRows((current) =>
      current.map((row) => ({
        ...row,
        amount: Math.max(0, Number(bulkAmount) || 0),
        type: bulkType
      }))
    );
  };

  const reset = () => {
    setYear(2026);
    setCode("04691");
    setEducation("other");
    setManufacturing(false);
    setStartingCumulativeTaxBase(0);
    setBulkAmount(50000);
    setBulkType("gross");
    setRows(createDefaultPayrollRows(50000, "gross"));
  };

  return (
    <div className="incentive-view">
      <section className="incentive-hero">
        <div className="incentive-hero__icon"><Calculator size={28} /></div>
        <div>
          <span className="incentive-eyebrow">Argevio mali simülasyon</span>
          <h1>Teşvik ve Bordro Simülasyonu</h1>
          <p>Standart bordro ile 05510, 04691 ve 05746 uygulamalarını aylık ve yıllık bazda karşılaştırın.</p>
        </div>
        <div className="incentive-hero__badge">
          <ShieldCheck size={18} />
          <span><strong>{year} kuralları</strong><small>Resmî parametrelerle sürümlendi</small></span>
        </div>
      </section>

      <section className="incentive-notice">
        <Info size={18} />
        <span>
          Bu ekran karar destek amaçlı bir <strong>simülasyondur</strong>. Kesin bordro, uygunluk şartları ve bildirge öncesinde mali müşavir kontrolü gerekir.
        </span>
      </section>

      <section className="incentive-settings-card">
        <div className="incentive-section-title">
          <div><Sparkles size={19} /><span><strong>Hesaplama ayarları</strong><small>Yıl, kanun ve personel koşullarını belirleyin</small></span></div>
          <button type="button" className="incentive-button incentive-button--ghost" onClick={reset}>
            <RotateCcw size={16} /> Sıfırla
          </button>
        </div>

        <div className="incentive-settings-grid">
          <label>
            <span>Yıl</span>
            <select value={year} onChange={(event) => setYear(Number(event.target.value))}>
              <option value={2026}>2026</option>
              <option value={2025}>2025</option>
            </select>
          </label>
          <label>
            <span>Kanun / teşvik</span>
            <select value={code} onChange={(event) => setCode(event.target.value)}>
              {INCENTIVE_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          </label>
          <label>
            <span>5746 eğitim durumu</span>
            <select
              value={education}
              onChange={(event) => setEducation(event.target.value)}
              disabled={code !== "05746"}
            >
              {EDUCATION_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          </label>
          <label>
            <span>Başlangıç kümülatif matrah</span>
            <div className="incentive-money-input"><b>₺</b><input type="number" min="0" step="1000" value={startingCumulativeTaxBase} onChange={(event) => setStartingCumulativeTaxBase(event.target.value)} /></div>
          </label>
        </div>

        <div className="incentive-toggle-row">
          <label className="incentive-toggle">
            <input type="checkbox" checked={manufacturing} onChange={(event) => setManufacturing(event.target.checked)} />
            <span aria-hidden="true" />
            <div><strong>İmalat sektörü 5 puan indirimi</strong><small>Uygun NACE ve diğer SGK şartları sağlanıyorsa açın.</small></div>
          </label>
          <div className="incentive-rule-pills">
            <span>Asgari ücret <b>{formatMoney(rules.minimumWage)}</b></span>
            <span>PEK tavanı <b>{formatMoney(rules.socialSecurityCeiling)}</b></span>
            <span>Standart indirim <b>{formatPercent(rules.standardDiscountRate)}</b></span>
          </div>
        </div>
      </section>

      <section className="incentive-summary-grid">
        <article className="is-mint"><BadgeTurkishLira size={21} /><span>Yıllık net ücret</span><strong>{formatMoney(result.totals.net)}</strong><small>Seçilen teşvik senaryosu</small></article>
        <article className="is-blue"><FileSpreadsheet size={21} /><span>İşveren maliyeti</span><strong>{formatMoney(result.totals.employerCost)}</strong><small>SGK destekleri sonrası</small></article>
        <article className="is-violet"><TrendingDown size={21} /><span>İşveren avantajı</span><strong>{formatMoney(result.totals.employerSaving)}</strong><small>Teşviksiz senaryoya göre</small></article>
        <article className="is-amber"><Sparkles size={21} /><span>Toplam kamu desteği</span><strong>{formatMoney(result.totals.publicSupport)}</strong><small>Vergi + damga + SGK</small></article>
      </section>

      <section className="incentive-breakdown-card">
        <div className="incentive-section-title">
          <div><CheckCircle2 size={19} /><span><strong>Teşvik kırılımı</strong><small>Seçilen senaryonun yıllık toplamları</small></span></div>
        </div>
        <div className="incentive-breakdown-grid">
          <div><span>SGK işveren desteği</span><strong>{formatMoney(result.totals.sgkIncentive)}</strong></div>
          <div><span>Gelir vergisi teşviki</span><strong>{formatMoney(result.totals.incomeTaxIncentive)}</strong></div>
          <div><span>Damga vergisi istisnası</span><strong>{formatMoney(result.totals.stampTaxIncentive)}</strong></div>
          <div><span>Çalışan kesintileri</span><strong>{formatMoney(result.totals.employeeDeductions)}</strong></div>
        </div>
      </section>

      <section className="incentive-table-card">
        <div className="incentive-section-title incentive-section-title--table">
          <div><FileSpreadsheet size={19} /><span><strong>12 aylık bordro planı</strong><small>Her ayı ayrı düzenleyebilir veya toplu değer uygulayabilirsiniz</small></span></div>
          <div className="incentive-bulk-edit">
            <select value={bulkType} onChange={(event) => setBulkType(event.target.value)} aria-label="Toplu ücret türü">
              <option value="gross">Brüt</option>
              <option value="net">Net</option>
            </select>
            <input type="number" min="0" step="1000" value={bulkAmount} onChange={(event) => setBulkAmount(event.target.value)} aria-label="Toplu ücret" />
            <button type="button" className="incentive-button incentive-button--primary" onClick={applyToAllMonths}>12 aya uygula</button>
          </div>
        </div>

        <div className="incentive-table-wrap">
          <table className="incentive-table">
            <thead>
              <tr>
                <th>Dönem</th>
                <th>Ücret türü</th>
                <th>Tutar</th>
                <th>Gün</th>
                <th>Teşvik günü</th>
                <th>Hesaplanan brüt</th>
                <th>Net ücret</th>
                <th>Vergi</th>
                <th>İşveren maliyeti</th>
                <th>Teşvik desteği</th>
              </tr>
            </thead>
            <tbody>
              {result.rows.map((item, index) => (
                <tr key={item.monthName}>
                  <td><strong>{item.monthName} {year}</strong></td>
                  <td>
                    <select value={rows[index].type} onChange={(event) => updateRow(index, "type", event.target.value)} aria-label={`${item.monthName} ücret türü`}>
                      <option value="gross">Brüt</option>
                      <option value="net">Net</option>
                    </select>
                  </td>
                  <td><input type="number" min="0" step="500" value={rows[index].amount} onChange={(event) => updateRow(index, "amount", event.target.value)} aria-label={`${item.monthName} ücret tutarı`} /></td>
                  <td><input className="is-day" type="number" min="1" max="30" value={rows[index].workDays} onChange={(event) => updateRow(index, "workDays", event.target.value)} aria-label={`${item.monthName} çalışma günü`} /></td>
                  <td><input className="is-day" type="number" min="0" max={rows[index].workDays || 30} value={rows[index].eligibleDays} onChange={(event) => updateRow(index, "eligibleDays", event.target.value)} disabled={!researchCode} aria-label={`${item.monthName} teşvik günü`} /></td>
                  <td>{formatMoney(item.selected.gross)}</td>
                  <td className="is-positive">{formatMoney(item.selected.net)}</td>
                  <td>{formatMoney(item.selected.payableIncomeTax + item.selected.payableStampTax)}</td>
                  <td>{formatMoney(item.selected.employerCostAfterIncentive)}</td>
                  <td className="is-highlight">{formatMoney(item.selected.totalPublicSupport)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="5">Yıllık toplam</td>
                <td>{formatMoney(result.totals.gross)}</td>
                <td>{formatMoney(result.totals.net)}</td>
                <td>{formatMoney(result.totals.payableTax)}</td>
                <td>{formatMoney(result.totals.employerCost)}</td>
                <td>{formatMoney(result.totals.publicSupport)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>

      <section className="incentive-method-card">
        <div>
          <span className="incentive-eyebrow">Hesaplama kapsamı</span>
          <h2>Şeffaf varsayımlar</h2>
          <p>Ücret, çalışan SGK ve işsizlik primi düşülerek kümülatif gelir vergisi tarifesine taşınır. Asgari ücret gelir ve damga vergisi istisnası ayrıca uygulanır. 04691 ve 05746 için uygun gün oranı, 40× asgari ücret sınırı ve kalan işveren SGK payının yarısı dikkate alınır.</p>
        </div>
        <div className="incentive-source-list">
          {OFFICIAL_SOURCES.map((source) => (
            <a key={source.url} href={source.url} target="_blank" rel="noreferrer">{source.label}<ExternalLink size={14} /></a>
          ))}
        </div>
      </section>
    </div>
  );
}

export default IncentiveCalculatorView;
